import React, { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { Location } from 'history';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { replace } from 'utils';

import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import useEffectOnce from 'hooks/use-effect-once';
import { useAuthContext } from 'state/auth';
import { useLogger } from 'state/logger';
import { RBIPlatform, platform } from 'utils/environment';
import { decodeBase64String, parseStringifiedJSON } from 'utils/parse-string';
import { routes } from 'utils/routing';

import { OpenInApp } from '../open-in-app';

import MissingLink from './missing-link';

export interface IValidateEmailForm {
  email: string;
}

// "sanitizes" ampersand characters
export const unescapeAmpersand = replace(/(&)(amp)?;?/g, '$1');

const parseUrlSearchParams = (location: Location) =>
  new URLSearchParams(unescapeAmpersand(location.search));

const AuthChallengeScreen = () => {
  const { signOut, validateLogin, originLocation, setOriginLocation } = useAuthContext();
  const logger = useLogger();
  const client = useApolloClient();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [confirmCorrectPlatform, setConfirmCorrectPlatform] = useState(false);
  const params = parseUrlSearchParams(location);
  const jwt = params.get('token') || '';
  const user = params.get('user') || '';
  const username = decodeBase64String({ value: user }) || '';

  const login = useCallback(
    async email => {
      setIsLoading(true);
      try {
        await validateLogin({ username: email, jwt });
        const path = originLocation || routes.base;
        setOriginLocation(null);
        // refetch offers when user navigates to /offers page
        client.reFetchObservableQueries();
        navigate(path, { replace: true });
      } catch {
        setIsLoading(false);
        navigate(routes.base, { replace: true });
      }
    },
    [validateLogin, jwt, originLocation, setOriginLocation, client, navigate]
  );

  useEffectOnce(() => {
    if (!jwt) {
      // No backend call and show the missing-link component
      signOut();
      setIsLoading(false);
      return;
    }

    const jwtBody = jwt.split('.')[1];
    const currentPlatform = platform();
    const originalRequestPlatform: RBIPlatform | undefined = parseStringifiedJSON({
      value: decodeBase64String({ value: jwtBody }) || '',
      defaultValue: {},
      onError: (error: Error) =>
        logger.warn({ error, message: 'Could not parse platform from auth jwt' }),
    }).platform;

    if (currentPlatform !== RBIPlatform.APP && originalRequestPlatform === RBIPlatform.APP) {
      setIsLoading(false);
      setConfirmCorrectPlatform(true);
      return;
    }

    login(username);
  });

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoadingContainer>
    );
  }

  if (confirmCorrectPlatform) {
    const handleContinueOnWebClick = () => {
      setIsLoading(true);
      setConfirmCorrectPlatform(false);

      login(username);
    };

    return (
      <OpenInApp
        continueInWebText={formatMessage({ id: 'authContinueWeb' })}
        onContinueOnWebClick={handleContinueOnWebClick}
        subHeaderText={formatMessage({ id: 'authContinueSubHeader' })}
      />
    );
  }

  return <MissingLink username={username} />;
};

export default AuthChallengeScreen;
